import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Building2, Layers, CircleDot, ChevronRight, Trash2, Edit, GripVertical, Loader2, Check } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { useFactory } from "@/core/hooks/useFactory";
import { useAuth } from "@/core/AuthContext";
import { useHasPermission } from "@/core/hooks/usePermissions";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/badge";
import { Skeleton } from "@/shared/ui/skeleton";
import { queryKeys } from "@/core/lib/queryKeys";
import {
  fetchAllFactoryAreas,
  fetchAllProductionLines,
  fetchAllStations,
  createFactoryArea,
  createProductionLine,
  createStation,
  updateFactoryArea,
  updateProductionLine,
  updateStation,
  deleteFactoryArea,
  deleteProductionLine,
  deleteStation,
  reorderProductionLines,
  reorderStations,
  type FactoryArea,
  type ProductionLine,
  type Station,
} from "@/core/api";

const COLORS = [
  "#0891b2", "#059669", "#d97706", "#dc2626", "#7c3aed", "#db2777", "#2563eb", "#475569"
];

// =============================================================================
// Debounced Reorder Hook
// =============================================================================

const DEBOUNCE_DELAY = 1500; // 1.5 seconds of inactivity before saving

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved';

function useDebouncedReorder<T extends { id: string }>(
  serverItems: T[],
  onSave: (orderedIds: string[]) => Promise<void>,
) {
  const [localItems, setLocalItems] = useState<T[]>(serverItems);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const pendingOrderRef = useRef<string[] | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync with server when server items change and we're not in the middle of editing
  useEffect(() => {
    if (saveStatus === 'idle' || saveStatus === 'saved') {
      const serverIds = serverItems.map(i => i.id).join(',');
      const localIds = localItems.map(i => i.id).join(',');
      if (serverIds !== localIds) {
        setLocalItems(serverItems);
      }
    }
  }, [serverItems, saveStatus, localItems]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    };
  }, []);

  const reorder = useCallback((fromIndex: number, toIndex: number) => {
    setLocalItems(prev => {
      const newItems = [...prev];
      const [movedItem] = newItems.splice(fromIndex, 1);
      newItems.splice(toIndex, 0, movedItem);
      
      // Store the pending order
      pendingOrderRef.current = newItems.map(item => item.id);
      
      return newItems;
    });

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
    }

    // Set status to pending (user is actively reordering)
    setSaveStatus('pending');

    // Set new debounced save
    timeoutRef.current = setTimeout(async () => {
      if (pendingOrderRef.current) {
        setSaveStatus('saving');
        try {
          await onSave(pendingOrderRef.current);
          pendingOrderRef.current = null;
          setSaveStatus('saved');
          // Show "saved" for 2 seconds then go back to idle
          savedTimeoutRef.current = setTimeout(() => {
            setSaveStatus('idle');
          }, 2000);
        } catch {
          setSaveStatus('idle');
          // On error, the onSave function should show a toast
        }
      }
    }, DEBOUNCE_DELAY);
  }, [onSave]);

  return { localItems, reorder, saveStatus };
}

// =============================================================================
// Save Status Indicator Component
// =============================================================================

function SaveStatusIndicator({ status }: { status: SaveStatus }) {
  if (status === 'idle') return null;
  
  return (
    <div className="flex items-center gap-1.5">
      {status === 'pending' && (
        <span className="text-xs text-muted-foreground">Unsaved</span>
      )}
      {status === 'saving' && (
        <>
          <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Saving...</span>
        </>
      )}
      {status === 'saved' && (
        <>
          <Check className="w-3.5 h-3.5 text-green-500" />
          <span className="text-xs text-green-500">Saved</span>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Reorderable List Component
// =============================================================================

interface DragHandleProps {
  onPointerDown: (e: React.PointerEvent) => void;
  className: string;
  style: React.CSSProperties;
}

interface ReorderableListProps<T extends { id: string }> {
  items: T[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  saveStatus: SaveStatus;
  renderItem: (item: T, dragHandleProps: DragHandleProps) => React.ReactNode;
  emptyMessage?: string;
}

function ReorderableList<T extends { id: string }>({
  items,
  onReorder,
  saveStatus,
  renderItem,
  emptyMessage = "No items",
}: ReorderableListProps<T>) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent, itemId: string) => {
    e.preventDefault();
    setDraggedId(itemId);
    
    // Capture pointer events
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggedId || !containerRef.current) return;
    
    const container = containerRef.current;
    const children = Array.from(container.children) as HTMLElement[];
    
    // Find the target position based on cursor position
    let targetIndex = items.findIndex(item => item.id === draggedId);
    
    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      
      if (e.clientY < midY) {
        targetIndex = i;
        break;
      } else if (i === children.length - 1) {
        targetIndex = i + 1;
      }
    }
    
    const draggedIndex = items.findIndex(item => item.id === draggedId);
    if (targetIndex !== dropTargetIndex && targetIndex !== draggedIndex && targetIndex !== draggedIndex + 1) {
      setDropTargetIndex(targetIndex);
    } else if (targetIndex === draggedIndex || targetIndex === draggedIndex + 1) {
      setDropTargetIndex(null);
    }
  }, [draggedId, items, dropTargetIndex]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!draggedId) return;
    
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    
    if (dropTargetIndex !== null) {
      const draggedIndex = items.findIndex(item => item.id === draggedId);
      
      // Adjust target index if needed
      const adjustedTarget = dropTargetIndex > draggedIndex ? dropTargetIndex - 1 : dropTargetIndex;
      
      if (draggedIndex !== adjustedTarget) {
        onReorder(draggedIndex, adjustedTarget);
      }
    }
    
    setDraggedId(null);
    setDropTargetIndex(null);
  }, [draggedId, dropTargetIndex, items, onReorder]);

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-6">{emptyMessage}</p>;
  }

  return (
    <div
      ref={containerRef}
      className="space-y-2 relative"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={() => {
        if (draggedId) {
          setDraggedId(null);
          setDropTargetIndex(null);
        }
      }}
    >
      {items.map((item, index) => {
        const isDragged = draggedId === item.id;
        const isDropTarget = dropTargetIndex === index;
        
        return (
          <div key={item.id} className="relative">
            {/* Drop indicator line */}
            {isDropTarget && (
              <div className="absolute -top-1 left-0 right-0 h-0.5 bg-primary rounded-full z-10" />
            )}
            <div
              className={cn(
                "transition-all duration-150",
                isDragged && "opacity-50 scale-[0.98]",
              )}
            >
              {renderItem(item, {
                onPointerDown: (e: React.PointerEvent) => handlePointerDown(e, item.id),
                className: cn(
                  "cursor-grab active:cursor-grabbing touch-none select-none",
                  saveStatus === 'saving' && "pointer-events-none"
                ),
                style: { touchAction: 'none' },
              })}
            </div>
            {/* Drop indicator at the end */}
            {index === items.length - 1 && dropTargetIndex === items.length && (
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full z-10" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Lines List Wrapper (with debounced reordering)
// =============================================================================

interface LinesListProps {
  lines: ProductionLine[];
  stations: Station[];
  selectedLine: string | null;
  onSelectLine: (id: string) => void;
  onEditLine: (line: ProductionLine) => void;
  onDeleteLine: (id: string) => void;
  onSaveOrder: (orderedIds: string[]) => Promise<void>;
}

function LinesList({ lines, stations, selectedLine, onSelectLine, onEditLine, onDeleteLine, onSaveOrder }: LinesListProps) {
  const { localItems, reorder, saveStatus } = useDebouncedReorder(lines, onSaveOrder);

  return (
    <>
      <SaveStatusIndicator status={saveStatus} />
      <ReorderableList
        items={localItems}
        onReorder={reorder}
        saveStatus={saveStatus}
        emptyMessage="No lines in this area"
        renderItem={(line, dragHandleProps) => (
          <div
            onClick={() => onSelectLine(line.id)}
            className={cn(
              "p-3 rounded-lg border cursor-pointer transition-all group",
              selectedLine === line.id
                ? "bg-accent border-primary"
                : "hover:bg-muted/50"
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  {...dragHandleProps}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                </div>
                <span className="font-medium text-sm">{line.name}</span>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onEditLine(line); }}>
                  <Edit className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); onDeleteLine(line.id); }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
            {line.description && (
              <p className="text-xs text-muted-foreground mt-1 ml-6 line-clamp-1">{line.description}</p>
            )}
            <div className="flex gap-2 flex-wrap mt-2 ml-6">
              <Badge variant="secondary" className="text-xs">
                {stations.filter(s => s.line_id === line.id).length} stations
              </Badge>
            </div>
          </div>
        )}
      />
    </>
  );
}

// =============================================================================
// Stations List Wrapper (with debounced reordering)
// =============================================================================

interface StationsListProps {
  stations: Station[];
  onEditStation: (station: Station) => void;
  onDeleteStation: (id: string) => void;
  onSaveOrder: (orderedIds: string[]) => Promise<void>;
}

function StationsList({ stations, onEditStation, onDeleteStation, onSaveOrder }: StationsListProps) {
  const { localItems, reorder, saveStatus } = useDebouncedReorder(stations, onSaveOrder);

  return (
    <>
      <SaveStatusIndicator status={saveStatus} />
      <ReorderableList
        items={localItems}
        onReorder={reorder}
        saveStatus={saveStatus}
        emptyMessage="No stations on this line"
        renderItem={(station, dragHandleProps) => (
          <div className="p-3 rounded-lg border hover:bg-muted/50 transition-all group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  {...dragHandleProps}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                </div>
                <span className="font-medium text-sm">{station.name}</span>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEditStation(station)}>
                  <Edit className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onDeleteStation(station.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {station.station_type && (
              <Badge variant="secondary" className="mt-2 ml-6 text-xs">{station.station_type}</Badge>
            )}
            {station.description && (
              <p className="text-xs text-muted-foreground mt-1 ml-6 line-clamp-1">{station.description}</p>
            )}
          </div>
        )}
      />
    </>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function FactoryStructure() {
  const { selectedFactoryId, selectedFactory } = useFactory();
  const { profile } = useAuth();
  const isAdmin = useHasPermission('factory.admin');
  const queryClient = useQueryClient();

  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [selectedLine, setSelectedLine] = useState<string | null>(null);

  // Dialog states
  const [areaDialog, setAreaDialog] = useState(false);
  const [lineDialog, setLineDialog] = useState(false);
  const [stationDialog, setStationDialog] = useState(false);
  const [editingArea, setEditingArea] = useState<FactoryArea | null>(null);
  const [editingLine, setEditingLine] = useState<ProductionLine | null>(null);
  const [editingStation, setEditingStation] = useState<Station | null>(null);

  // Form states
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formColor, setFormColor] = useState(COLORS[0]);
  const [formStationType, setFormStationType] = useState("");
  

  // Queries
  const { data: areas = [], isLoading: areasLoading } = useQuery({
    queryKey: ['factory-areas', selectedFactoryId],
    queryFn: () => fetchAllFactoryAreas(selectedFactoryId!),
    enabled: !!selectedFactoryId
  });

  const { data: lines = [], isLoading: linesLoading } = useQuery({
    queryKey: ['production-lines', selectedFactoryId],
    queryFn: () => fetchAllProductionLines(selectedFactoryId!),
    enabled: !!selectedFactoryId
  });

  const { data: stations = [], isLoading: stationsLoading } = useQuery({
    queryKey: ['stations', selectedFactoryId],
    queryFn: () => fetchAllStations(selectedFactoryId!),
    enabled: !!selectedFactoryId
  });

  // Mutations for Areas
  const createAreaMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFactoryId || !selectedFactory) throw new Error('No factory selected');
      await createFactoryArea({
        tenant_id: selectedFactory.tenant_id,
        factory_id: selectedFactoryId,
        name: formName,
        description: formDescription || null,
        color: formColor,
        sort_order: areas.length,
        is_active: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.factoryAreas(selectedFactoryId) });
      toast.success("Area created");
      resetForm();
      setAreaDialog(false);
    },
    onError: () => toast.error("Failed to create area")
  });

  const updateAreaMutation = useMutation({
    mutationFn: async () => {
      if (!editingArea) return;
      await updateFactoryArea(editingArea.id, {
        name: formName,
        description: formDescription || null,
        color: formColor,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.factoryAreas(selectedFactoryId) });
      toast.success("Area updated");
      resetForm();
      setAreaDialog(false);
    },
    onError: () => toast.error("Failed to update area")
  });

  const deleteAreaMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteFactoryArea(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.factoryAreas(selectedFactoryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productionLines(selectedFactoryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations(selectedFactoryId) });
      toast.success("Area deleted");
      if (selectedArea) setSelectedArea(null);
    },
    onError: () => toast.error("Failed to delete area")
  });

  // Mutations for Lines
  const createLineMutation = useMutation({
    mutationFn: async () => {
      if (!selectedArea || !selectedFactoryId || !selectedFactory) throw new Error('No area selected');
      await createProductionLine({
        tenant_id: selectedFactory.tenant_id,
        factory_id: selectedFactoryId,
        area_id: selectedArea,
        name: formName,
        description: formDescription || null,
        sort_order: lines.filter(l => l.area_id === selectedArea).length,
        is_active: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productionLines(selectedFactoryId) });
      toast.success("Line created");
      resetForm();
      setLineDialog(false);
    },
    onError: () => toast.error("Failed to create line")
  });

  const updateLineMutation = useMutation({
    mutationFn: async () => {
      if (!editingLine) return;
      await updateProductionLine(editingLine.id, {
        name: formName,
        description: formDescription || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productionLines(selectedFactoryId) });
      toast.success("Line updated");
      resetForm();
      setLineDialog(false);
    },
    onError: () => toast.error("Failed to update line")
  });

  const deleteLineMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteProductionLine(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productionLines(selectedFactoryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations(selectedFactoryId) });
      toast.success("Line deleted");
      if (selectedLine) setSelectedLine(null);
    },
    onError: () => toast.error("Failed to delete line")
  });

  // Reorder Lines function (returns promise for debounced hook)
  const handleReorderLines = useCallback(async (orderedIds: string[]) => {
    await reorderProductionLines(orderedIds);
    queryClient.invalidateQueries({ queryKey: queryKeys.productionLines(selectedFactoryId) });
  }, [queryClient, selectedFactoryId]);

  // Mutations for Stations
  const createStationMutation = useMutation({
    mutationFn: async () => {
      if (!selectedLine || !selectedFactoryId || !selectedFactory) throw new Error('No line selected');
      await createStation({
        tenant_id: selectedFactory.tenant_id,
        factory_id: selectedFactoryId,
        line_id: selectedLine,
        name: formName,
        description: formDescription || null,
        station_type: formStationType || null,
        sort_order: stations.filter(s => s.line_id === selectedLine).length,
        is_active: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stations(selectedFactoryId) });
      toast.success("Station created");
      resetForm();
      setStationDialog(false);
    },
    onError: () => toast.error("Failed to create station")
  });

  const updateStationMutation = useMutation({
    mutationFn: async () => {
      if (!editingStation) return;
      await updateStation(editingStation.id, {
        name: formName,
        description: formDescription || null,
        station_type: formStationType || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stations(selectedFactoryId) });
      toast.success("Station updated");
      resetForm();
      setStationDialog(false);
    },
    onError: () => toast.error("Failed to update station")
  });

  const deleteStationMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteStation(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stations(selectedFactoryId) });
      toast.success("Station deleted");
    },
    onError: () => toast.error("Failed to delete station")
  });

  // Reorder Stations function (returns promise for debounced hook)
  const handleReorderStations = useCallback(async (orderedIds: string[]) => {
    await reorderStations(orderedIds);
    queryClient.invalidateQueries({ queryKey: queryKeys.stations(selectedFactoryId) });
  }, [queryClient, selectedFactoryId]);

  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setFormColor(COLORS[0]);
    setFormStationType("");
    setEditingArea(null);
    setEditingLine(null);
    setEditingStation(null);
  };

  const openEditArea = (area: FactoryArea) => {
    setEditingArea(area);
    setFormName(area.name);
    setFormDescription(area.description || "");
    setFormColor(area.color);
    setAreaDialog(true);
  };

  const openEditLine = (line: ProductionLine) => {
    setEditingLine(line);
    setFormName(line.name);
    setFormDescription(line.description || "");
    setLineDialog(true);
  };

  const openEditStation = (station: Station) => {
    setEditingStation(station);
    setFormName(station.name);
    setFormDescription(station.description || "");
    setFormStationType(station.station_type || "");
    setStationDialog(true);
  };

  const canEdit = isAdmin || profile?.is_super_admin;

  if (!selectedFactoryId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold">No Factory Selected</h2>
        <p className="text-muted-foreground">Select a factory to view its structure</p>
        <Link to="/">
          <Button variant="outline" className="mt-4">Go to Dashboard</Button>
        </Link>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold">Access Denied</h2>
        <p className="text-muted-foreground">Only factory admins can access this page</p>
        <Link to="/">
          <Button variant="outline" className="mt-4">Go to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const filteredLines = selectedArea ? lines.filter(l => l.area_id === selectedArea) : [];
  const filteredStations = selectedLine ? stations.filter(s => s.line_id === selectedLine) : [];
  const selectedAreaData = areas.find(a => a.id === selectedArea);

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground">Factory Structure</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Design your factory hierarchy: Areas → Lines → Stations
        </p>
      </div>

      {/* Visual Org Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Areas Column */}
        <Card className="border-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Areas
              </CardTitle>
              <Dialog open={areaDialog} onOpenChange={(open) => { if (!open) resetForm(); setAreaDialog(open); }}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1">
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Add</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingArea ? "Edit Area" : "New Area"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g., Assembly" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="Optional description" />
                    </div>
                    <div className="space-y-2">
                      <Label>Color</Label>
                      <div className="flex gap-2 flex-wrap">
                        {COLORS.map(c => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setFormColor(c)}
                            className={cn(
                              "w-8 h-8 rounded-lg transition-all",
                              formColor === c && "ring-2 ring-offset-2 ring-primary"
                            )}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { resetForm(); setAreaDialog(false); }}>Cancel</Button>
                    <Button onClick={() => editingArea ? updateAreaMutation.mutate() : createAreaMutation.mutate()} disabled={!formName.trim()}>
                      {editingArea ? "Update" : "Create"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {areasLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-14" />
                <Skeleton className="h-14" />
              </div>
            ) : areas.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No areas yet</p>
            ) : (
              areas.map(area => (
                <div
                  key={area.id}
                  onClick={() => { setSelectedArea(area.id); setSelectedLine(null); }}
                  className={cn(
                    "p-3 rounded-lg border cursor-pointer transition-all group",
                    selectedArea === area.id
                      ? "bg-accent border-primary"
                      : "hover:bg-muted/50"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: area.color }} />
                      <span className="font-medium text-sm">{area.name}</span>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEditArea(area); }}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); deleteAreaMutation.mutate(area.id); }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  {area.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{area.description}</p>
                  )}
                  <div className="flex gap-2 flex-wrap mt-2">
                    <Badge variant="secondary" className="text-xs">
                      {lines.filter(l => l.area_id === area.id).length} lines
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Lines Column */}
        <Card className="border-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                Lines
                {selectedAreaData && (
                  <Badge variant="outline" style={{ borderColor: selectedAreaData.color, color: selectedAreaData.color }}>
                    {selectedAreaData.name}
                  </Badge>
                )}
              </CardTitle>
              <Dialog open={lineDialog} onOpenChange={(open) => { if (!open) resetForm(); setLineDialog(open); }}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1" disabled={!selectedArea}>
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Add</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingLine ? "Edit Line" : "New Line"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g., Line A" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="Optional description" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { resetForm(); setLineDialog(false); }}>Cancel</Button>
                    <Button onClick={() => editingLine ? updateLineMutation.mutate() : createLineMutation.mutate()} disabled={!formName.trim()}>
                      {editingLine ? "Update" : "Create"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {!selectedArea ? (
              <p className="text-sm text-muted-foreground text-center py-6">Select an area first</p>
            ) : linesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-14" />
                <Skeleton className="h-14" />
              </div>
            ) : (
              <LinesList
                key={selectedArea} // Reset component state when area changes
                lines={filteredLines}
                stations={stations}
                selectedLine={selectedLine}
                onSelectLine={setSelectedLine}
                onEditLine={openEditLine}
                onDeleteLine={(id) => deleteLineMutation.mutate(id)}
                onSaveOrder={handleReorderLines}
              />
            )}
          </CardContent>
        </Card>

        {/* Stations Column */}
        <Card className="border-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <CircleDot className="w-5 h-5 text-primary" />
                Stations
                {selectedLine && lines.find(l => l.id === selectedLine) && (
                  <Badge variant="outline">
                    {lines.find(l => l.id === selectedLine)?.name}
                  </Badge>
                )}
              </CardTitle>
              <Dialog open={stationDialog} onOpenChange={(open) => { if (!open) resetForm(); setStationDialog(open); }}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1" disabled={!selectedLine}>
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Add</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingStation ? "Edit Station" : "New Station"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g., Station 1" />
                    </div>
                    <div className="space-y-2">
                      <Label>Type (optional)</Label>
                      <Input value={formStationType} onChange={(e) => setFormStationType(e.target.value)} placeholder="e.g., Assembly, QC, Packaging" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="Optional description" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { resetForm(); setStationDialog(false); }}>Cancel</Button>
                    <Button onClick={() => editingStation ? updateStationMutation.mutate() : createStationMutation.mutate()} disabled={!formName.trim()}>
                      {editingStation ? "Update" : "Create"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {!selectedLine ? (
              <p className="text-sm text-muted-foreground text-center py-6">Select a line first</p>
            ) : stationsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-14" />
                <Skeleton className="h-14" />
              </div>
            ) : (
              <StationsList
                key={selectedLine} // Reset component state when line changes
                stations={filteredStations}
                onEditStation={openEditStation}
                onDeleteStation={(id) => deleteStationMutation.mutate(id)}
                onSaveOrder={handleReorderStations}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
